/* eslint-disable react-hooks/exhaustive-deps */
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material'
import {useEffect, useState} from 'react'
import {updateSiteSettings} from '../AuthApi'
import {SiteSettingModel} from '../../../models/SiteSettingModel'
import {DatePickerInput} from '../../../components/inputs/DatePickerInput'

export const OpenRegistrationModal = ({
  open,
  onClose,
  onUpdate,
  data,
}: {
  open: boolean
  onClose: () => void
  onUpdate: (data: SiteSettingModel) => void
  data: SiteSettingModel | null
}) => {
  const initialState = {
    startedAt: data?.startedAt ? new Date(data.startedAt) : null,
    endedAt: data?.endedAt ? new Date(data.endedAt) : null,
    previewAt: data?.previewAt ? new Date(data.previewAt) : null,
  }

  const [formData, setFormData] = useState(initialState)

  const [errors, setErrors] = useState<{startedAt?: string; endedAt?: string; previewAt?: string}>(
    {}
  )
  const [touched, setTouched] = useState<{
    startedAt?: boolean
    endedAt?: boolean
    previewAt?: boolean
  }>({})
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    if (open) {
      setFormData(initialState)
      setErrors({})
      setTouched({})
    }
  }, [open, data])

  useEffect(() => {
    const newErrors: {startedAt?: string; endedAt?: string; previewAt?: string} = {}

    if (!formData.startedAt) newErrors.startedAt = 'Started At is required'
    if (!formData.endedAt) newErrors.endedAt = 'Ended At is required'
    if (!formData.previewAt) newErrors.previewAt = 'Preview At is required'

    if (formData.startedAt && formData.endedAt && formData.startedAt > formData.endedAt) {
      newErrors.startedAt = 'Started At cannot be later than Ended At'
    }
    if (formData.previewAt && formData.endedAt && formData.previewAt > formData.endedAt) {
      newErrors.previewAt = 'Preview At cannot be later than Ended At'
    }

    setErrors(newErrors)

    setIsValid(Object.keys(newErrors).length === 0)
  }, [formData])

  const handleChange = (name: keyof typeof formData) => (value: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    setTouched((prev) => ({...prev, [name]: true}))
  }

  const handleSubmit = async () => {
    if (!isValid) return

    try {
      const formattedData: SiteSettingModel = {
        startedAt: formData.startedAt ? formData.startedAt.toISOString() : '',
        endedAt: formData.endedAt ? formData.endedAt.toISOString() : '',
        previewAt: formData.previewAt ? formData.previewAt.toISOString() : '',
      }

      await updateSiteSettings(formattedData)
      onUpdate(formattedData)
      onClose()
    } catch (error) {
      console.error('Data is invalid', error)
    }
  }

  const shouldDisableDate = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Site Settings</DialogTitle>
      <DialogContent>
        <Box sx={{mt: 1, mb: 1}}>
          <DatePickerInput
            label='Started At'
            inputFormat='YYYY/MM/DD'
            value={formData.startedAt}
            onChange={handleChange('startedAt')}
            shouldDisableDate={shouldDisableDate}
            fullWidth
            allowClear
            error={!!errors.startedAt && touched.startedAt}
            helperText={touched.startedAt ? errors.startedAt : ''}
          />
        </Box>
        <Box sx={{my: 2}}>
          <DatePickerInput
            label='Ended At'
            inputFormat='YYYY/MM/DD'
            value={formData.endedAt}
            onChange={handleChange('endedAt')}
            fullWidth
            allowClear
            shouldDisableDate={shouldDisableDate}
            error={!!errors.endedAt && touched.endedAt}
            helperText={touched.endedAt ? errors.endedAt : ''}
          />
        </Box>
        <Box sx={{my: 2}}>
          <DatePickerInput
            label='Preview At'
            inputFormat='YYYY/MM/DD'
            value={formData.previewAt}
            onChange={handleChange('previewAt')}
            shouldDisableDate={shouldDisableDate}
            fullWidth
            allowClear
            error={!!errors.previewAt && touched.previewAt}
            helperText={touched.previewAt ? errors.previewAt : ''}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='secondary'>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant='contained' color='primary' disabled={!isValid}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  )
}
